import { Controller } from 'stimulus';
import { canonicalFontName } from '@/js/util/tiptap/extensions/_fonts';

export default class extends Controller {
  static targets = [
    'editor',
    'list',
    'fontSelectHeader',
    'fontFamily',
    'color',
    'leftAlignButton',
    'centerAlignButton',
    'rightAlignButton',
    'boldButton',
    'italicButton',
    'underlineButton',
    'orderedListButton',
    'unorderedListButton',
    'textStyleButton',
    'fontSize',
    'lineHeight',
  ];

  static values = {
    defaultText: String,
  };

  notifyInput({ target }) {
    this.notify('input', target);
  }

  notifyChange({ target }) {
    this.notify('change', target);
  }

  notify(prefix, target) {
    window.ed.featureActionsService.add('text_properties_edited');
    const { name, form } = target;
    const values = {};
    // implements same workaround as rails did for unchecked checkboxes, allowing it to be completely
    // controlled by HTML, see: https://apidock.com/rails/ActionView/Helpers/FormHelper/check_box
    for (const [key, value] of new FormData(form).entries()) {
      let input;
      if (form.elements[key] instanceof HTMLCollection) {
        input = Array.from(form.elements[key]).find((el) => el.value === value);
      } else {
        input = form.elements[key];
      }
      if (input.type === 'checkbox' && !input.checked) {
        continue;
      }
      values[key] = value;
    }

    this.dispatch(`${prefix}-${name}`, { detail: { value: values[name] } });
  }

  updateFontSelect(event) {
    this.fontSelectHeaderTarget.innerText = event.target.value;
  }

  showTextMenu() {
    this.editorTarget.classList.add('hidden');
    this.listTarget.classList.remove('hidden');
  }

  prepareTextPanel(event) {
    const overlay = document.querySelector('.item-overlay');
    const item = document.querySelector('.item--text.selected');
    if (!item) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent('legacy:text-editor-requested', {
        detail: { item, prepareTextPanel: true },
      })
    );
    item.classList.add('selection:bg-transparent');
    item.classList.remove('!z-(--editor-layer-tiptap-editor)');
    setTimeout(() => {
      if (overlay.dataset.doubleclickCount === '2') {
        document.dispatchEvent(new CustomEvent('legacy:text-editor-focus-end'));
      } else {
        document.dispatchEvent(new CustomEvent('legacy:text-editor-dismissed'));
      }
      item.classList.remove('selection:bg-transparent');
    }, 10);
  }

  showTextControls(event) {
    this.dispatch('switch-sidepanel', {
      detail: { sidepanelId: 'text', active: true },
      target: document,
      prefix: false,
    });
    this.editorTarget.classList.remove('hidden');
    this.listTarget.classList.add('hidden');
  }

  updateTextControls(event) {
    const textProperties = event.detail;
    this.#populateBold(textProperties);
    this.#populateItalic(textProperties);
    this.#populateUnderline(textProperties);
    this.#populateSizeInput(textProperties);
    this.#populateFontFamily(textProperties);
    this.#populateLineHeightInput(textProperties);
    this.#populateColor(textProperties);
    this.#populateAlignButtons(textProperties);
    this.#populateListStyle(textProperties);
    this.#populateTextStyle(textProperties);
  }

  #populateFontFamily({ font }) {
    const fontName = (canonicalFontName(font) || font).replace(/['"]/g, '');

    this.fontSelectHeaderTarget.innerText = fontName;
    this.fontFamilyTargets.find((el) => el.value === fontName).checked = true;
  }

  #populateColor({ color }) {
    this.colorTarget.value = color;
  }

  #populateAlignButtons({ align }) {
    if (!align) {
      ['left', 'center', 'right'].forEach((position) => {
        this.targets.find(`${position}AlignButton`).checked = false;
      });
      return;
    }
    this.targets.find(`${align}AlignButton`).checked = true;
  }

  #populateBold({ weight }) {
    this.boldButtonTarget.checked = weight;
  }

  #populateItalic({ fontStyle }) {
    this.italicButtonTarget.checked = fontStyle;
  }

  #populateUnderline({ textDecoration }) {
    this.underlineButtonTarget.checked = textDecoration;
  }

  #populateListStyle({ listStyle }) {
    this.orderedListButtonTarget.checked = listStyle === 'ordered';
    this.unorderedListButtonTarget.checked = listStyle === 'unordered';
  }

  #populateTextStyle({ heading, heading2, heading3, heading4 }) {
    let activeStyle = 'paragraph';
    if (heading) {
      activeStyle = 'heading';
    } else if (heading2) {
      activeStyle = 'heading2';
    } else if (heading3) {
      activeStyle = 'heading3';
    } else if (heading4) {
      activeStyle = 'heading4';
    }

    this.textStyleButtonTargets.forEach((button) => {
      button.checked = button.value === activeStyle;
    });
  }

  #populateSizeInput({ fontSize }) {
    fontSize = parseInt(fontSize);

    this.fontSizeTargets.forEach((input) => (input.value = fontSize));
  }

  #populateLineHeightInput({ lineHeight }) {
    lineHeight = parseFloat(lineHeight);

    this.lineHeightTargets.forEach((input) => (input.value = lineHeight));
  }
}
